// ============================================================
// HOUSE OF CARDS — SceneEngine v3 — REAL OBJECTS
// The scaffolding comes down. Every hotspot now renders as its
// hand-drawn SVG object from HotspotArt.js, standing in the
// first-person room, with a small nameplate beneath (the card
// frame survives as the nameplate + the inspect closeup —
// the card is still the brand, it just holds art now).
// Hover an object: it catches the light and shows its desc.
// All gameplay logic identical to v1/v2.
// ============================================================
import React, { useRef, useState, useEffect } from "react";
import { RM, ROOM_TINTS, ROOM_SCENES, veinGlow, ITEM_RARITY, RARITY_COLORS } from "./theme";
import { DOORS, ITEMS } from "./roomData";
import { HOTSPOT_ART, DoorSprite, ITEM_ICONS } from "./HotspotArt";
import { solutionItemsFor } from "./puzzleEngine";
import { eyesVisible, isShadowObject, shadowObjectStyle, ensureShadowKeyframes } from "./unstableEyes";
import "./roomRig.css";

// Side walls reuse a quiet dark paneling derived from the palette
// (kept out of ROOM_SCENES so theme.js stays untouched).
const SIDE_WALL = [
  "repeating-linear-gradient(0deg, rgba(0,0,0,0.28) 0 2px, transparent 2px 110px)",
  `linear-gradient(180deg, ${RM.char2} 0%, ${RM.char0} 100%)`,
].join(", ");

// ── Hall corridor geometry ───────────────────────────────────
// The six doors mount onto the receding side walls. Depth comes
// from each door's y in roomData (30 = far, 55 = mid, 80 = near);
// side comes from x (<50 = left). Far doors sit toward center and
// smaller; near doors push to the edge and larger — modest, real.
const CORRIDOR = {
  30: { x: 26, y: 50, s: 0.80 }, // far  — pulled outward + down, clear of the Mouth
  55: { x: 17, y: 64, s: 0.92 }, // mid
  80: { x: 9,  y: 79, s: 1.04 }, // near
};
function corridorPlacement(d) {
  const slot = CORRIDOR[d.y] || CORRIDOR[55];
  const left = d.x < 50 ? slot.x : 100 - slot.x;
  return { left, top: slot.y, scale: slot.s };
}

// Gentle depth cue for ordinary room hotspots: higher on the
// frame (smaller y) reads a touch further away and smaller.
const depthScale = (y) => +(0.86 + (y / 100) * 0.26).toFixed(3);

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function SceneEngine({
  room,            // room object from ROOMS
  state,           // full game state (flags, unlockedDoors, inventory)
  armedItem,       // item id armed from Inventory, or null
  eyesActive,      // bool — Unstable Eyes on? gates unstable/stable hotspots
  cursed,          // bool — Cursed (hard) Mode? controls pocket-picker hinting
  onAction,        // (action, hotspot, itemId?) => void  — "take"|"press"|"useItem"
  onDoor,          // (doorId) => void           — hall only
  onChoice,        // (choiceId) => void         — in-world drive choices
  onScare,         // () => void                 — fire a non-lethal jumpscare
  onMaybeScare,    // (weight:number) => void    — maybe fire a REAL scare (tense moment)
  onArm,           // (itemId|null) => void      — arm/disarm (kept for compat)
  onOverlayChange, // (open:boolean) => void     — report any open text/picker (pauses psychosis)
  onLeave,         // () => void                 — back to hall
}) {
  const [closeup, setCloseup] = useState(null); // hotspot being inspected
  const [picker, setPicker] = useState(null);   // hotspot the pocket picker is targeting
  const [firedScares, setFiredScares] = useState({}); // hotspotId → true (once-per-session)
  const rigRef = useRef(null);
  const tint = ROOM_TINTS[room.id] || "transparent";
  const scene = ROOM_SCENES[room.id] || ROOM_SCENES.hall;
  const isHall = room.id === "hall";

  // ── Camera sway: the skull turns a few degrees with the mouse
  const onMove = (e) => {
    if (REDUCED || !rigRef.current) return;
    const x = e.clientX / window.innerWidth - 0.5;
    const y = e.clientY / window.innerHeight - 0.5;
    rigRef.current.style.setProperty("--camx", `${(x * 3.4).toFixed(2)}deg`);
    rigRef.current.style.setProperty("--camy", `${(-y * 2.4).toFixed(2)}deg`);
  };
  const onStill = () => {
    if (!rigRef.current) return;
    rigRef.current.style.setProperty("--camx", "0deg");
    rigRef.current.style.setProperty("--camy", "0deg");
  };

  // Register the shadow-object breathing keyframes once (no-op if present).
  ensureShadowKeyframes();

  // Any open text box (closeup) or pocket picker pauses the game (item 1):
  // report it up so PsychosisEngine freezes while the player reads/decides.
  useEffect(() => {
    onOverlayChange && onOverlayChange(!!closeup || !!picker);
  }, [closeup, picker, onOverlayChange]);

  const visible = room.hotspots.filter((h) => {
    if (!eyesVisible(h, eyesActive)) return false;     // Unstable Eyes gate
    if (h.hiddenUntil && !state.flags[h.hiddenUntil]) return false;
    if (h.removedBy && state.flags[h.removedBy]) return false;
    return true;
  });

  // Open a hotspot's closeup, firing a jumpscare first if it's flagged
  // with `scare`. scare = { chance?: 0..1, once?: bool, lethal?: bool }.
  // once = fire at most one time per visit to this room screen.
  const openHotspot = (h) => {
    const sc = h.scare;
    if (sc && onScare) {
      const blocked = sc.once && firedScares[h.id];
      const roll = sc.chance == null ? 1 : Math.random();
      if (!blocked && roll <= (sc.chance == null ? 1 : sc.chance)) {
        if (sc.once) setFiredScares((m) => ({ ...m, [h.id]: true }));
        onScare();
      }
    }
    // Leaning in to look at a TRULY wrong thing — a shadow object only
    // the Unstable Eyes can see — is a genuinely tense moment, so it's
    // allowed to (rarely) trigger a real scare. Ordinary closeups don't.
    if (onMaybeScare && isShadowObject(h)) onMaybeScare(0.9);
    setCloseup(h);
  };

  const CloseupArt = closeup ? HOTSPOT_ART[closeup.id] : null;

  // ── UNSTABLE EYES — the whole world shifts (item 5) ──────────
  // It isn't just objects appearing/vanishing anymore. The moment the
  // eyes open, the ENTIRE house — every room, every hall — re-grades
  // into the true palette: near-black, bruise-violet, and a sick red.
  // We do it as a screen-wide grade so it costs nothing per room and
  // covers walls, floor, fog, sprites and all. Everything that was
  // there is still there — just wearing the house's real colors.
  const eyesGrade = eyesActive
    ? {
        filter:
          "brightness(0.74) contrast(1.22) saturate(1.05) sepia(0.45) hue-rotate(248deg)",
        transition: "filter 900ms ease",
      }
    : { transition: "filter 900ms ease" };

  return (
    <div style={S.frame} onMouseMove={onMove} onMouseLeave={onStill}>
      {/* Everything in the world gets graded together under the eyes. */}
      <div style={{ ...S.worldGrade, ...eyesGrade }}>
      {/* ── The room itself: back wall, ceiling, floor, in true 3D */}
      <div className="room3d">
        <div className="room-rig" ref={rigRef}>
          <div className="plane backwall" style={{ background: scene.back }} />
          <div className="plane ceiling" style={{ background: scene.ceil }} />
          <div className="plane floor" style={{ background: scene.floor }} />
          <div className="plane leftwall" style={{ background: SIDE_WALL }} />
          <div className="plane rightwall" style={{ background: SIDE_WALL }} />
        </div>
      </div>
      <div className="room-veins" />
      <div className="wall-shade" />
      <div
        className="room-fog"
        style={{ background: `radial-gradient(ellipse at 50% 72%, ${scene.fog} 0%, transparent 62%)` }}
      />
      </div>

      {/* ── UNSTABLE EYES OVERLAY STACK (only with eyes open) ──────
          A layered, breathing treatment — not a flat tint:
            1. eyes-wash    — the bruise color field (violet→red→black)
            2. eyes-veins   — living capillaries crawling the walls
            3. eyes-bleed   — chromatic red/violet fringe at the edges
            4. eyes-vignette— heavy dark frame so the center pulls focus
          All pointer-transparent; all reduced-motion safe via App.css. */}
      {eyesActive && (
        <>
          <div style={S.eyesWash} className="eyes-wash" />
          <div style={S.eyesVeins} className="eyes-veins" />
          <div style={S.eyesBleed} className="eyes-bleed" />
          <div style={S.eyesVignette} className="eyes-vignette" />
        </>
      )}

      {/* (Room title + flavor header removed — the rooms speak for
          themselves now. We'll reintroduce room identity later via a
          small in-world sign/interactable if we ever want it.) */}

      {/* Hall doors — drawn doors, mounted along the corridor walls */}
      {isHall &&
        DOORS.map((d) => {
          const open = state.unlockedDoors.includes(d.id) || d.lockedBy === null;
          const sealed = d.lockedBy === "LOCKED_V1";
          const place = corridorPlacement(d);
          return (
            <div
              key={d.id}
              onClick={() => onDoor(d.id)}
              className="hotspot-obj"
              style={{ left: `${place.left}%`, top: `${place.top}%`, "--s": place.scale }}
            >
              <DoorSprite open={open} sealed={sealed} />
              <div className="nameplate plaque" style={{ borderColor: open ? RM.moldDim : RM.charBorder }}>
                <div className="nameplate-title" style={{ color: open ? RM.moldBright : RM.bone }}>
                  {d.label}
                </div>
                <div className="nameplate-desc">
                  {open ? "unlocked" : sealed ? "sealed (future wing)" : "locked"}
                </div>
              </div>
            </div>
          );
        })}

      {/* Hotspots — the objects themselves */}
      {visible.map((h) => {
        const isMouth = h.special === "mouth";
        const Art = HOTSPOT_ART[h.id];
        const shadow = isShadowObject(h); // exists only in the unstable world
        // Mouth Door anchors the end of the hall — hold it large.
        const s = isMouth ? 1.04 : depthScale(h.y);
        const canUse = h.useTarget || (h.actions || []).includes("use");
        return (
          <div
            key={h.id}
            onClick={() => {
              // Armed item (legacy path) still works: use it straight away.
              if (armedItem) return onAction("useItem", h, armedItem);
              const hasCloseup = (h.actions || []).includes("inspect") || (h.roomChoices || []).length;
              // Pure interactable (no reading, no choices) → straight to the
              // pocket picker, saving the player a trip (item 6).
              if (canUse && !hasCloseup) return setPicker(h);
              if ((h.actions || []).includes("press")) return onAction("press", h);
              openHotspot(h);
            }}
            className={
              (isMouth ? "mouth-breathe hotspot-obj" : "hotspot-obj") +
              (h.compact ? " compact" : "")
            }
            style={{ left: `${h.x}%`, top: `${h.y}%`, "--s": s }}
          >
            {Art ? (
              <div style={shadow ? shadowObjectStyle() : undefined}>
                <Art flags={state.flags} />
              </div>
            ) : null}
            <div
              className="nameplate plaque"
              style={{
                borderColor: armedItem || canUse ? RM.moldBright : isMouth ? RM.vein : RM.charBorder,
                ...(isMouth ? { boxShadow: veinGlow() } : {}),
                ...(Art ? {} : { maxWidth: 190 }),
              }}
            >
              <div
                className="nameplate-title"
                style={isMouth ? { color: RM.fleshGlow, fontSize: 15 } : undefined}
              >
                {h.label}
                {canUse && !armedItem ? <span style={S.useTag}> · USE</span> : null}
              </div>
              {/* no sprite yet? show desc always, like the old cards */}
              <div className={Art ? "nameplate-desc" : "nameplate-desc nameplate-desc-open"}>
                {h.desc}
              </div>
            </div>
          </div>
        );
      })}

      {/* Leave room */}
      {!isHall && (
        <div onClick={onLeave} style={S.leaveBtn} className="hotspot-card">
          ← back to the Hall
        </div>
      )}

      {/* Inspect close-up */}
      {closeup && (
        <div style={S.closeupVeil} onClick={() => setCloseup(null)}>
          <div style={S.closeupCard} className="plaque" onClick={(e) => e.stopPropagation()}>
            {/* Art plate — user images via closeup.art override the sprite */}
            {closeup.art ? (
              <img src={closeup.art} alt="" style={S.plateImg} onError={(e) => (e.target.style.display = "none")} />
            ) : (
              <div
                style={{
                  ...S.plateArt,
                  background: `radial-gradient(ellipse at 50% 42%, ${tint} 0%, transparent 72%), linear-gradient(180deg, #181009 0%, #0a0705 100%)`,
                }}
              >
                {CloseupArt ? (
                  <div style={S.plateSpriteWrap}>
                    <CloseupArt flags={state.flags} />
                  </div>
                ) : (
                  <>
                    <div style={S.plateMonogram}>{closeup.label.charAt(0)}</div>
                    <div style={S.plateHint}>· the house remembers what this looks like ·</div>
                  </>
                )}
              </div>
            )}
            <div style={S.closeupTitle}>{closeup.label}</div>
            <div style={S.closeupBody}>{closeup.closeup || closeup.desc}</div>

            {/* In-world choices — each seeds a drive toward an ending.
                Hidden once taken (its `once` flag is set) or until its
                `requiresFlag` gate is met. */}
            {(() => {
              const choices = (closeup.roomChoices || []).filter((c) => {
                if (c.requiresFlag && !state.flags[c.requiresFlag]) return false;
                if (c.once && state.flags[c.once]) return false;
                return true;
              });
              if (!choices.length) return null;
              return (
                <div style={S.choiceWrap}>
                  {choices.map((c) => (
                    <button
                      key={c.id}
                      style={S.choiceBtn}
                      onClick={() => {
                        onChoice(c.id);
                        setCloseup(null);
                      }}
                    >
                      <span style={S.choiceLabel}>{c.label}</span>
                      {c.hint && <span style={S.choiceHint}>{c.hint}</span>}
                    </button>
                  ))}
                </div>
              );
            })()}

            <div style={S.closeupActions}>
              {closeup.actions.includes("take") && (
                <button
                  style={S.btn}
                  onClick={() => {
                    onAction("take", closeup);
                    setCloseup(null);
                  }}
                >
                  {closeup.takeLabel || "Take"}
                </button>
              )}
              {(closeup.useTarget || (closeup.actions || []).includes("use")) && (
                <button
                  style={S.btn}
                  onClick={() => {
                    const h = closeup;
                    setCloseup(null);
                    setPicker(h); // open the pocket picker on this object
                  }}
                >
                  Use…
                </button>
              )}
              <button style={S.btnDim} onClick={() => setCloseup(null)}>
                Step back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── POCKET PICKER ───────────────────────────────────────
          The "Use" path. Opening this freezes the game (psychosis
          paused upstream). Shows the player's pockets:
            • Easy Mode: the item(s) that actually work here are lit;
              everything else is dimmed. No solution carried → all dim.
            • Cursed (hard) Mode: every item shown normal, no hints.
          Picking an item uses it on the target hotspot. */}
      {picker && (
        <PocketPicker
          hotspot={picker}
          inventory={state.inventory || []}
          cursed={cursed}
          solutions={cursed ? null : solutionItemsFor(picker.id, state)}
          onPick={(itemId) => {
            const h = picker;
            setPicker(null);
            onAction("useItem", h, itemId);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
}

// ── Pocket picker overlay ──────────────────────────────────────
// Centered list of carried items. Easy Mode dims non-solutions and
// blocks tapping them; Cursed Mode shows all live, no help (item 6).
function PocketPicker({ hotspot, inventory, cursed, solutions, onPick, onClose }) {
  const monogram = (name = "?") =>
    name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <div style={S.closeupVeil} onClick={onClose}>
      <div style={S.pickerCard} className="plaque" onClick={(e) => e.stopPropagation()}>
        <div style={S.pickerTitle}>Use what on {hotspot.label}?</div>
        {inventory.length === 0 ? (
          <div style={S.pickerEmpty}>Your pockets are empty. The house notices empty hands.</div>
        ) : (
          <div style={S.pickerGrid}>
            {inventory.map((id) => {
              const it = ITEMS[id];
              const rarity = RARITY_COLORS[ITEM_RARITY[id]] || RM.boneDim;
              const Icon = ITEM_ICONS[id];
              // Easy Mode: only solution items are usable; the rest dim.
              const lit = cursed || (solutions && solutions.includes(id));
              const dim = !cursed && !lit;
              return (
                <button
                  key={id}
                  title={it?.desc}
                  onClick={() => onPick(id)}
                  className="hotspot-card plaque"
                  style={{
                    ...S.pickerItem,
                    borderColor: lit ? RM.moldBright : rarity,
                    opacity: dim ? 0.34 : 1,
                    filter: dim ? "grayscale(0.7)" : "none",
                    boxShadow: lit ? veinGlow(RM.moldBright) : `inset 0 0 12px rgba(0,0,0,0.6)`,
                  }}
                >
                  <div style={{ ...S.pickerSlot, color: rarity, borderColor: `${rarity}55` }}>
                    {Icon ? <Icon /> : monogram(it?.name)}
                  </div>
                  <div style={{ ...S.pickerItemName, color: lit ? RM.moldBright : RM.bone }}>
                    {it?.name || id}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <button style={S.btnDim} onClick={onClose}>Step back</button>
      </div>
    </div>
  );
}

const S = {
  frame: { position: "absolute", inset: 0, overflow: "hidden" },
  worldGrade: { position: "absolute", inset: 0 },
  eyesWash: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 4,
    mixBlendMode: "multiply",
    background: [
      "radial-gradient(ellipse at 50% 38%, rgba(86,20,64,0.40) 0%, transparent 58%)",
      "radial-gradient(ellipse at 50% 104%, rgba(140,16,34,0.42) 0%, transparent 56%)",
      "radial-gradient(ellipse at 14% 16%, rgba(58,12,72,0.34) 0%, transparent 46%)",
      "radial-gradient(ellipse at 86% 22%, rgba(58,12,72,0.30) 0%, transparent 46%)",
      "linear-gradient(180deg, rgba(8,3,12,0.40) 0%, rgba(4,1,3,0.58) 100%)",
    ].join(", "),
  },
  // Living capillaries — a faint, screen-blended web that crawls the
  // walls and pulses with the room's heartbeat. Two stacked conic/radial
  // gradient fields give an organic, non-repeating vein read.
  eyesVeins: {
    position: "absolute",
    inset: "-8%",
    pointerEvents: "none",
    zIndex: 5,
    mixBlendMode: "screen",
    opacity: 0.5,
    background: [
      "radial-gradient(circle at 22% 30%, rgba(196,86,90,0.22) 0 2px, transparent 3px)",
      "radial-gradient(circle at 78% 64%, rgba(196,86,90,0.20) 0 2px, transparent 3px)",
      "repeating-radial-gradient(ellipse 60% 90% at 30% 50%, transparent 0 26px, rgba(150,28,52,0.10) 27px 28px, transparent 29px 60px)",
      "repeating-radial-gradient(ellipse 90% 60% at 70% 60%, transparent 0 30px, rgba(110,35,80,0.10) 31px 32px, transparent 33px 70px)",
    ].join(", "),
  },
  // Chromatic edge bleed — red on one diagonal, violet on the other,
  // pooling in the corners. Sells the "vision is wrong" fringe.
  eyesBleed: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 6,
    mixBlendMode: "screen",
    opacity: 0.55,
    background: [
      "radial-gradient(ellipse at 0% 0%, rgba(150,20,40,0.30) 0%, transparent 30%)",
      "radial-gradient(ellipse at 100% 100%, rgba(150,20,40,0.28) 0%, transparent 30%)",
      "radial-gradient(ellipse at 100% 0%, rgba(96,24,120,0.30) 0%, transparent 30%)",
      "radial-gradient(ellipse at 0% 100%, rgba(96,24,120,0.28) 0%, transparent 30%)",
    ].join(", "),
  },
  // Heavy bruise vignette so the center holds focus and the frame
  // closes in like tunnel vision.
  eyesVignette: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 7,
    background:
      "radial-gradient(ellipse at 50% 46%, transparent 26%, rgba(20,2,16,0.5) 70%, rgba(4,1,6,0.92) 100%)",
  },
  header: {
    position: "absolute",
    top: 40,
    left: 0,
    right: 0,
    textAlign: "center",
    pointerEvents: "none",
    zIndex: 5,
  },
  roomName: {
    fontFamily: RM.fontT,
    fontWeight: 700,
    fontSize: 30,
    letterSpacing: 8,
    textTransform: "uppercase",
    color: RM.bone,
    textShadow: `0 0 28px ${RM.vein}, 0 2px 0 #000`,
  },
  headerRule: {
    fontFamily: RM.fontD,
    fontSize: 11,
    letterSpacing: 6,
    color: RM.boneFaint,
    margin: "2px 0",
  },
  roomFlavor: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 14,
    color: RM.boneDim,
    textShadow: "0 1px 2px #000",
  },
  leaveBtn: {
    position: "absolute",
    left: 18,
    top: 14,
    padding: "8px 14px",
    background: "rgba(13,9,7,0.9)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 8,
    fontFamily: RM.fontD,
    fontSize: 13,
    color: RM.boneDim,
    cursor: "pointer",
    zIndex: 30,
  },
  closeupVeil: {
    position: "absolute",
    inset: 0,
    background: "rgba(5,3,2,0.82)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 45,
  },
  closeupCard: {
    width: "min(460px, 90vw)",
    maxHeight: "88vh",
    overflowY: "auto",
    padding: "22px 26px 26px",
    background: "linear-gradient(180deg, #1a110c 0%, #100a07 100%)",
    border: `1px solid ${RM.vein}`,
    borderRadius: 14,
    boxShadow: `${veinGlow()}, 0 30px 80px rgba(0,0,0,0.7)`,
  },
  plateArt: {
    minHeight: 130,
    padding: "14px 0",
    borderRadius: 9,
    border: `1px solid ${RM.charBorder}`,
    marginBottom: 16,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    boxShadow: "inset 0 0 40px rgba(0,0,0,0.6)",
  },
  plateSpriteWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.6))",
  },
  plateImg: {
    width: "100%",
    height: 160,
    objectFit: "cover",
    borderRadius: 9,
    border: `1px solid ${RM.charBorder}`,
    marginBottom: 16,
  },
  plateMonogram: {
    fontFamily: RM.fontT,
    fontWeight: 900,
    fontSize: 44,
    color: "rgba(216,203,179,0.16)",
    lineHeight: 1,
  },
  plateHint: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 10,
    letterSpacing: 2,
    color: RM.boneFaint,
  },
  closeupTitle: {
    fontFamily: RM.fontT,
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: 3,
    textTransform: "uppercase",
    color: RM.bone,
    marginBottom: 12,
    borderBottom: `1px solid ${RM.charBorder}`,
    paddingBottom: 8,
  },
  closeupBody: {
    fontFamily: RM.font,
    fontSize: 16,
    color: RM.boneDim,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  closeupActions: { display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" },
  useTag: {
    fontFamily: RM.fontD,
    fontSize: 11,
    letterSpacing: 2,
    color: RM.moldBright,
  },
  pickerCard: {
    width: "min(440px, 90vw)",
    maxHeight: "84vh",
    overflowY: "auto",
    padding: "22px 24px 24px",
    background: "linear-gradient(180deg, #1a110c 0%, #100a07 100%)",
    border: `1px solid ${RM.moldDim}`,
    borderRadius: 14,
    boxShadow: `${veinGlow(RM.moldBright)}, 0 30px 80px rgba(0,0,0,0.7)`,
  },
  pickerTitle: {
    fontFamily: RM.fontD,
    fontSize: 18,
    letterSpacing: 2,
    color: RM.bone,
    marginBottom: 16,
    borderBottom: `1px solid ${RM.charBorder}`,
    paddingBottom: 10,
  },
  pickerEmpty: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 14,
    color: RM.boneFaint,
    marginBottom: 16,
  },
  pickerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  pickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 12px 8px 8px",
    border: "1px solid",
    borderRadius: 9,
    background: "linear-gradient(180deg, #1a110c, #100a07)",
    fontFamily: RM.font,
    fontSize: 14,
    cursor: "pointer",
    textAlign: "left",
    transition: "all 120ms ease",
  },
  pickerSlot: {
    width: 34,
    height: 34,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderRadius: 7,
    background: "rgba(0,0,0,0.45)",
    fontFamily: RM.fontT,
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 1,
  },
  pickerItemName: { lineHeight: 1.1 },
  choiceWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 16,
  },
  choiceBtn: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "10px 14px",
    background: "linear-gradient(180deg, #20140d, #140c08)",
    border: `1px solid ${RM.vein}`,
    borderRadius: 9,
    color: RM.bone,
    fontFamily: RM.font,
    fontSize: 15,
    cursor: "pointer",
    textAlign: "left",
  },
  choiceLabel: { color: RM.bone },
  choiceHint: {
    fontFamily: RM.fontD,
    fontStyle: "italic",
    fontSize: 12,
    color: RM.boneFaint,
    letterSpacing: 1,
  },
  btn: {
    padding: "9px 18px",
    background: RM.moldDim,
    border: `1px solid ${RM.moldBright}`,
    borderRadius: 8,
    color: RM.bone,
    fontFamily: RM.fontD,
    fontSize: 14,
    cursor: "pointer",
  },
  btnDim: {
    padding: "9px 18px",
    background: "transparent",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 8,
    color: RM.boneDim,
    fontFamily: RM.fontD,
    fontSize: 14,
    cursor: "pointer",
  },
};
